import React, { useMemo, useState, useCallback } from "react";
import moment from "moment";
import { Formik, Form, Field } from "formik";
import styled, { css } from "styled-components";
import deburr from "lodash/deburr";
import { PluginStore } from "graylog-web-plugin/plugin";
import useIndexSetTemplateDefaults from "components/indices/IndexSetTemplates/hooks/useIndexSetTemplateDefaults";
import AppConfig from "util/AppConfig";
import { FormikInput, FormSubmit, Section, Spinner, TimeUnitInput } from "components/common";
import HideOnCloud from "util/conditional/HideOnCloud";
import { Alert, Col, Row } from "components/bootstrap";
import "components/indices/rotation";
import "components/indices/retention";
import { prepareDataTieringConfig, prepareDataTieringInitialValues } from "components/indices/data-tiering";
import IndexRetentionProvider from "components/indices/contexts/IndexRetentionProvider";
import useHistory from "routing/useHistory";
import IndexSetProfileConfiguration from "components/indices/IndexSetProfileConfiguration";
import useFeature from "hooks/useFeature";
import useIndexSet from "components/indices/hooks/useIndexSet";
import isIndexFieldTypeChangeAllowed from "components/indices/helpers/isIndexFieldTypeChangeAllowed";
import useProductName from "brand-customization/useProductName";
import HiddenFieldWrapper from "components/indices/IndexSetConfigurationForm/HiddenFieldWrapper";
import IndexSetReadOnlyConfiguration from "components/indices/IndexSetConfigurationForm/IndexSetReadOnlyConfiguration";
import IndexSetRotationRetentionConfigurationSection from "components/indices/IndexSetConfigurationForm/IndexSetRotationRetentionConfigurationSection";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import { parseFieldRestrictions } from "components/indices/helpers/fieldRestrictions";
const FlexWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.md};
  `
);
const SubmitWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
export const transformTitleToPrefix = (title) => {
  if (!title) return "";
  return deburr(title).trim().toLowerCase().replace(/[^a-z0-9_+-]+/g, "-").replace(/^[_+-]+/, "").replace(/-+$/, "").replace(/-{2,}/g, "-");
};
const IndexSetConfigurationForm = ({
  indexSet: initialIndexSet = void 0,
  rotationStrategies,
  retentionStrategies,
  retentionStrategiesContext,
  create = false,
  onUpdate,
  cancelLink,
  submitButtonText,
  submitLoadingText = void 0
}) => {
  const history = useHistory();
  const productName = useProductName();
  const ignoreFieldRestrictions = isPermitted(useCurrentUser().permissions, "indexsets_field_restrictions:edit");
  const [fieldTypeRefreshIntervalUnit, setFieldTypeRefreshIntervalUnit] = useState("seconds");
  const { loadingIndexSetTemplateDefaults, indexSetTemplateDefaults } = useIndexSetTemplateDefaults();
  const [indexSet] = useIndexSet(initialIndexSet);
  const [autoFillPrefix, setAutoFillPrefix] = useState(true);
  const getFieldRestrictions = useCallback(() => parseFieldRestrictions(indexSet == null ? void 0 : indexSet.field_restrictions), [indexSet]);
  const immutableFields = getFieldRestrictions().immutableFields;
  const hiddenFields = getFieldRestrictions().hiddenFields;
  const isCloud = AppConfig.isCloud();
  const enableDataTieringCloud = useFeature("data_tiering_cloud");
  const initialSegment = () => {
    if (hiddenFields.includes("data_tiering")) return "legacy";
    if (hiddenFields.includes("legacy")) return "data_tiering";
    if (indexSet == null ? void 0 : indexSet.use_legacy_rotation) return "legacy";
    return "data_tiering";
  };
  const [selectedRetentionSegment, setSelectedRetentionSegment] = useState(initialSegment());
  const isDataTieringImmutable = useMemo(() => immutableFields.includes("data_tiering"), [immutableFields]);
  const isDataTieringLocked = isDataTieringImmutable && !ignoreFieldRestrictions;
  const prepareRetentionConfigBeforeSubmit = useCallback(
    (values) => {
      const indexSetValues = values;
      if (!create) {
        delete indexSetValues.index_prefix;
        delete indexSetValues.index_analyzer;
        delete indexSetValues.creation_date;
        delete indexSetValues.can_be_default;
        delete indexSetValues.default;
      }
      const legacyConfig = {
        ...indexSetValues,
        data_tiering: indexSetTemplateDefaults.data_tiering,
        use_legacy_rotation: true
      };
      if (isCloud && !enableDataTieringCloud) {
        return legacyConfig;
      }
      if (selectedRetentionSegment === "legacy") {
        return legacyConfig;
      }
      const configWithDataTiering = {
        ...indexSetValues,
        data_tiering: prepareDataTieringConfig(values.data_tiering, PluginStore, isDataTieringLocked)
      };
      if (loadingIndexSetTemplateDefaults || !indexSetTemplateDefaults)
        return { ...configWithDataTiering, use_legacy_rotation: false };
      const legacyindexSetTemplateDefaults = {
        rotation_strategy_class: indexSetTemplateDefaults.rotation_strategy_class,
        rotation_strategy: indexSetTemplateDefaults.rotation_strategy,
        retention_strategy_class: indexSetTemplateDefaults.retention_strategy_class,
        retention_strategy: indexSetTemplateDefaults.retention_strategy
      };
      return { ...configWithDataTiering, ...legacyindexSetTemplateDefaults, use_legacy_rotation: false };
    },
    [
      create,
      loadingIndexSetTemplateDefaults,
      indexSetTemplateDefaults,
      selectedRetentionSegment,
      enableDataTieringCloud,
      isCloud,
      isDataTieringLocked
    ]
  );
  const saveConfiguration = (values) => onUpdate(prepareRetentionConfigBeforeSubmit(values));
  const onFieldTypeRefreshIntervalChange = (intervalValue, unit, name, onChange, setFieldValue) => {
    onChange(name, moment.duration(intervalValue, unit).asMilliseconds());
    setFieldValue(name, moment.duration(intervalValue, unit).asMilliseconds());
    setFieldTypeRefreshIntervalUnit(unit);
  };
  const handleTitleChange = useCallback(
    (event, setFieldValue) => {
      if (!create || !autoFillPrefix) return;
      const transformedPrefix = transformTitleToPrefix(event.target.value);
      setFieldValue("index_prefix", transformedPrefix);
    },
    [create, autoFillPrefix]
  );
  const handlePrefixChange = useCallback(() => {
    setAutoFillPrefix(false);
  }, []);
  const detailsSectionRenderable = () => {
    if (create) return true;
    const detailsFieldsEdit = [
      "shards",
      "replicas",
      "index_optimization_max_num_segments",
      "index_optimization_disabled",
      "field_type_refresh_interval"
    ];
    return detailsFieldsEdit.filter((fieldName) => !hiddenFields.includes(fieldName)).length > 0;
  };
  if (!indexSet) return null;
  const onCancel = () => history.push(cancelLink);
  if (loadingIndexSetTemplateDefaults) return /* @__PURE__ */ React.createElement(Spinner, null);
  const prepareInitialValues = () => {
    if (indexSet.data_tiering) {
      return {
        ...indexSet,
        data_tiering: prepareDataTieringInitialValues(indexSet.data_tiering, PluginStore, isDataTieringLocked)
      };
    }
    return indexSet;
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(Formik, { validateOnMount: true, onSubmit: saveConfiguration, enableReinitialize: true, initialValues: prepareInitialValues() }, ({ isValid, setFieldValue, isSubmitting, values }) => /* @__PURE__ */ React.createElement(IndexRetentionProvider, null, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(FlexWrapper, null, /* @__PURE__ */ React.createElement(Section, { title: "Configuration Information" }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      label: "Title",
      id: "title",
      name: "title",
      help: "Descriptive name of the index set.",
      onChange: (event) => handleTitleChange(event, setFieldValue),
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      id: "description",
      label: "Description",
      name: "description",
      help: "Add a description of this index set.",
      required: true
    }
  )), detailsSectionRenderable() && /* @__PURE__ */ React.createElement(Section, { title: "Details" }, create && /* @__PURE__ */ React.createElement(
    IndexSetReadOnlyConfiguration,
    {
      hiddenFields,
      immutableFields,
      ignoreFieldRestrictions,
      onPrefixChange: handlePrefixChange
    }
  ), /* @__PURE__ */ React.createElement(HideOnCloud, null, /* @__PURE__ */ React.createElement(
    HiddenFieldWrapper,
    {
      hiddenFields,
      ignoreFieldRestrictions
    },
    /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "shards",
        label: "Index Shards",
        name: "shards",
        help: "Number of search cluster Shards used per index in this Index Set. Increasing the Index Shards improves the search cluster write speed of data stored to this Index Set by distributing the active write Index over multiple search nodes. Increasing the Index Shards can degrade search performance and increases the memory footprint of the Index. This value should not be set higher than the number of search nodes.",
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("shards")) && !ignoreFieldRestrictions
      }
    ),
    /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "replicas",
        label: "Index Replica",
        name: "replicas",
        help: "Number of search cluster Replica Shards used per Index in this Index Set. Adding Replica Shards improves search performance during parallel reads of the index, such as occurs on dashboards, and is a component of HA and backup strategy. Each Replica Shard set multiplies the storage requirement and memory footprint of the index. This value should not be set higher than the number of search nodes, and typically not higher than 1.                                   ",
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("replicas")) && !ignoreFieldRestrictions
      }
    ),
    /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "max-number-segments",
        label: "Maximum Number of Segments",
        name: "index_optimization_max_num_segments",
        minLength: 1,
        help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " Maximum number of segments per Search Cluster Index after optimization (force merge). Setting higher values decreases the compression ratio of Index Optimization."),
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("index_optimization_max_num_segments")) && !ignoreFieldRestrictions
      }
    ),
    /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "checkbox",
        id: "index-optimization-disabled",
        label: "Disable Index Optimization after Rotation",
        name: "index_optimization_disabled",
        help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " Index Optimization is a compression process that occurs after an active Index has been rotated and reduces the size of an Index on disk. It manifests as a CPU intensive maintenance task performed by the search cluster after Index rotation. Compressing Indexes improves search performance and decreases the storage footprint of Index Sets."),
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("index_optimization_disabled")) && !ignoreFieldRestrictions
      }
    ),
    /* @__PURE__ */ React.createElement(Field, { name: "field_type_refresh_interval" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: "Field Type Refresh Interval",
        help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " How often the Field Type Information for the active write Index will be updated. Setting this value higher can marginally reduce search cluster overhead and improve performance, but will result in new data messages longer to be searchable in ", productName, "."),
        value: moment.duration(value, "milliseconds").as(fieldTypeRefreshIntervalUnit),
        unit: fieldTypeRefreshIntervalUnit.toUpperCase(),
        units: ["SECONDS", "MINUTES"],
        update: (intervalValue, unit) => onFieldTypeRefreshIntervalChange(intervalValue, unit, name, onChange, setFieldValue),
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("field_type_refresh_interval")) && !ignoreFieldRestrictions
      }
    ))
  ))), /* @__PURE__ */ React.createElement(
    IndexSetRotationRetentionConfigurationSection,
    {
      values,
      indexSet,
      retentionStrategies,
      retentionStrategiesContext,
      rotationStrategies,
      hiddenFields,
      immutableFields,
      ignoreFieldRestrictions,
      isCloud,
      enableDataTieringCloud,
      selectedRetentionSegment,
      setSelectedRetentionSegment
    }
  ), isIndexFieldTypeChangeAllowed(indexSet) && /* @__PURE__ */ React.createElement(Section, { title: "Field Type Profile" }, /* @__PURE__ */ React.createElement(Field, { name: "field_type_profile" }, ({ field: { name, value } }) => /* @__PURE__ */ React.createElement(
    IndexSetProfileConfiguration,
    {
      value,
      onChange: (profileId) => {
        setFieldValue(name, profileId);
      },
      name
    }
  ))), /* @__PURE__ */ React.createElement(Section, { title: "Important Note" }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "These changes do not apply to any existing indices. They only apply to newly created indices. To apply this to the current index set immediately, rotate the index.")), /* @__PURE__ */ React.createElement(SubmitWrapper, null, /* @__PURE__ */ React.createElement(
    FormSubmit,
    {
      disabledSubmit: !isValid,
      submitButtonText,
      submitLoadingText,
      isSubmitting,
      isAsyncSubmit: true,
      displayCancel: true,
      onCancel
    }
  ))))))));
};
export default IndexSetConfigurationForm;
